<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { rateLimitMessage, type Phase } from '../lib/phase'
import type { LoginPayload, SessionChoice, WindowState } from '../types'
import HomeShell from './HomeShell.vue'
import PhaseStatus from './PhaseStatus.vue'

// One login form for participants, proctors and admins; the server decides the role.
// Plain HTML on purpose: the student path must stay small.
const props = withDefaults(
  defineProps<{
    busy: boolean
    error: string
    notice?: string
    phase?: Phase | null
    phaseSince?: number
    /** Sessions to choose from after a `session_required` reply, else null. */
    sessions?: SessionChoice[] | null
    /** Epoch ms until which the server asked us to wait (429), else null. */
    retryUntil?: number | null
  }>(),
  { notice: '', phase: null, phaseSince: 0, sessions: null, retryUntil: null },
)

const emit = defineEmits<{
  submit: [payload: LoginPayload]
  schedule: [payload: LoginPayload]
  cancelSessionChoice: []
}>()

// Secrets live only in component memory: never in storage, never logged.
const loginId = ref('')
const secret = ref('')
const showSecret = ref(false)
const firstInput = ref<HTMLInputElement | null>(null)

// Countdown timer exists only while a 429 wait is active.
const now = ref(Date.now())
let timer: number | null = null
watch(
  () => props.retryUntil,
  (until) => {
    if (timer !== null) window.clearInterval(timer)
    timer = null
    if (until === null) return
    now.value = Date.now()
    timer = window.setInterval(() => {
      now.value = Date.now()
      if (now.value >= until && timer !== null) {
        window.clearInterval(timer)
        timer = null
      }
    }, 1000)
  },
  { immediate: true },
)
onBeforeUnmount(() => {
  if (timer !== null) window.clearInterval(timer)
})

const waitSeconds = computed(() =>
  props.retryUntil === null ? 0 : Math.max(0, Math.ceil((props.retryUntil - now.value) / 1000)),
)
const rateLimited = computed(() => waitSeconds.value > 0)

const canSubmit = computed(
  () => !props.busy && !rateLimited.value && loginId.value.trim() !== '' && secret.value.trim() !== '',
)

function payload(sessionId?: string): LoginPayload {
  return {
    kind: 'unified',
    identifier: loginId.value.trim(),
    secret: secret.value.trim(),
    ...(sessionId ? { session_id: sessionId } : {}),
  }
}

function submit(): void {
  if (!canSubmit.value) return
  emit('submit', payload())
}

function showSchedule(): void {
  if (!canSubmit.value) return
  emit('schedule', payload())
}

function choose(session: SessionChoice): void {
  if (props.busy || rateLimited.value) return
  emit('submit', payload(session.id))
}

const stateLabel: Record<WindowState, string> = {
  open: 'Sedang dibuka',
  upcoming: 'Belum dimulai',
  closed: 'Sudah ditutup',
}
const order: Record<WindowState, number> = { open: 0, upcoming: 1, closed: 2 }
const sortedSessions = computed(() =>
  props.sessions
    ? [...props.sessions].sort((a, b) => (order[a.window_state] ?? 3) - (order[b.window_state] ?? 3))
    : [],
)

onMounted(() => {
  firstInput.value?.focus()
})
</script>

<template>
  <HomeShell>
    <h2 class="title">Masuk ke Platform CBT</h2>

    <p v-if="notice" class="banner banner--info" role="status">{{ notice }}</p>
    <p v-if="error" class="banner banner--error" role="alert">{{ error }}</p>
    <template v-if="rateLimited">
      <p class="banner banner--warning" aria-hidden="true">{{ rateLimitMessage(waitSeconds) }}</p>
      <p class="sr-only" role="status">Terlalu banyak percobaan. Mohon tunggu sebentar.</p>
    </template>

    <section v-if="sessions" class="session-choice" aria-labelledby="session-choice-title">
      <h3 id="session-choice-title">Pilih sesi ujian</h3>
      <p class="field-help">Akun Anda terdaftar di lebih dari satu sesi. Pilih sesi yang akan dikerjakan.</p>
      <ul class="session-list">
        <li v-for="session in sortedSessions" :key="session.id">
          <button class="session-option" type="button" :disabled="busy || rateLimited" @click="choose(session)">
            <strong>{{ session.title }}</strong>
            <span class="session-option__state" :class="`session-option__state--${session.window_state}`">
              {{ stateLabel[session.window_state] ?? session.window_state }}
            </span>
          </button>
        </li>
      </ul>
      <button class="link-button" type="button" :disabled="busy" @click="emit('cancelSessionChoice')">
        Kembali
      </button>
    </section>

    <form v-else class="form" autocomplete="off" @submit.prevent="submit">
      <label for="login-id">ID Pengguna</label>
      <input
        id="login-id"
        ref="firstInput"
        v-model="loginId"
        type="text"
        name="login-id"
        autocomplete="off"
        autocapitalize="none"
        spellcheck="false"
        :disabled="busy"
      />

      <label for="login-secret">Kata Sandi</label>
      <div class="secret">
        <input
          id="login-secret"
          v-model="secret"
          :type="showSecret ? 'text' : 'password'"
          name="login-secret"
          autocomplete="off"
          autocapitalize="none"
          spellcheck="false"
          :disabled="busy"
        />
        <button
          class="secret__toggle"
          type="button"
          :aria-label="showSecret ? 'Sembunyikan kata sandi' : 'Tampilkan kata sandi'"
          :aria-pressed="showSecret"
          @click="showSecret = !showSecret"
        >
          <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <template v-if="showSecret">
              <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
              <path d="M1 1l22 22" />
            </template>
            <template v-else>
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
              <circle cx="12" cy="12" r="3" />
            </template>
          </svg>
        </button>
      </div>

      <button class="button button--primary button--wide" type="submit" :disabled="!canSubmit">
        <span v-if="busy" class="spinner" aria-hidden="true" />
        {{ busy ? 'Mohon tunggu…' : 'Login' }}
      </button>
      <button class="link-button schedule-link" type="button" :disabled="!canSubmit" @click="showSchedule">
        Lihat jadwal ujian saya
      </button>
    </form>

    <PhaseStatus :phase="phase" :since="phaseSince" />
  </HomeShell>
</template>

<style scoped>
.title {
  margin: 0 0 18px;
  font-size: 1.15rem;
  font-weight: 650;
  text-align: center;
}

.form {
  display: grid;
  gap: 6px;
}

.form label {
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--ink-soft);
}

.form input {
  width: 100%;
  height: 48px;
  margin-bottom: 10px;
  padding: 0 14px;
  border: 1px solid var(--line-strong);
  border-radius: 10px;
  background: #fff;
  color: var(--ink);
  font-size: 1rem;
  transition:
    border-color 140ms ease,
    box-shadow 140ms ease;
}

.form input:focus {
  outline: none;
  border-color: var(--accent);
  box-shadow: 0 0 0 4px rgba(15, 118, 110, 0.12);
}

.secret {
  position: relative;
}

.secret input {
  padding-right: 48px;
}

.secret__toggle {
  position: absolute;
  top: 0;
  right: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 48px;
  padding: 0;
  border: 0;
  background: none;
  color: var(--ink-soft);
  cursor: pointer;
}

.secret__toggle:hover,
.secret__toggle:focus-visible {
  color: var(--accent-strong);
}

.form .button {
  margin-top: 6px;
}

.schedule-link {
  justify-self: center;
  margin-top: 4px;
}

.session-list {
  display: grid;
  gap: 8px;
  margin: 12px 0;
  padding: 0;
  list-style: none;
}

.session-option {
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 14px;
  border: 1px solid var(--line);
  border-radius: 12px;
  background: var(--surface-soft);
  color: inherit;
  font: inherit;
  text-align: left;
  cursor: pointer;
}

.session-option:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.session-option__state {
  flex: none;
  font-size: 0.78rem;
  color: var(--muted);
}

.session-option__state--open {
  color: var(--success);
  font-weight: 700;
}
</style>
